import { logError } from '@core/logging';
import { BaseApiResponse, ChatSettings, SlideDeck, SlideDeckStatus, WebinarDataWithAssets } from '@core/swagger-client';
import {
    Chapter,
    ChatChannel,
    ChatMessage,
    ClientType,
    Exam,
    LiveStatus,
    Participant,
    ParticipantAnswers,
    Poll,
    PollVoteSlim,
    PollWithVotes,
    Subtitle,
    SubtitlesTrack,
    Survey,
    VideoLayout
} from '@core/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { cloneDeep, isEqual } from 'lodash-es';
import { Duration } from 'luxon';
import { ActivePollStatus, ChatRejection, ConnectionStatus, JoinWebinarRequest } from './live-webinar-slice';

export interface ChapterWithTimestamp extends Chapter {
    timestamp: number;
}

export interface OnDemandWebinarState {
    isInitialized: boolean;

    webinarId: string | null;
    webinarData: WebinarDataWithAssets | null;
    onDemandData: OnDemandData | null;

    serverTime: string | null;

    videoLayout: VideoLayout;

    activeSlideNumber: number | null;
    slideDeck: SlideDeck;
    activeChapterId: string | null;
    // Non-optional on purpose: while this was optional, TypeScript happily accepted an initialState
    // without it — and onDemandLeaveWebinar's Object.assign(state, initialState) then could not
    // clear it, leaking the previous webinar's chapter (and its URL hash) into the next one.
    activeChapter: Chapter | null;
    chapters: ChapterWithTimestamp[];

    polls: PollWithVotes[];
    hasVotedOnPoll: boolean;
    activePoll: PollWithVotes | null;
    activePollId: string | null;
    activePollStatus: ActivePollStatus;
    myPollVotes: PollVoteSlim[];
    /** The polls as they are now; the timeline holds them as they were when recorded. */
    currentPolls: Poll[];

    dataConnection: ConnectionStatus;

    me: Participant | null;

    unreadChatMessages: number;
    chatChannels: { [channelId: string]: ChatChannel };
    chatSettings: ChatSettings;

    subtitles: SubtitlesTrack[];
    activeSubtitleTrackId?: string | null;
    activeSubtitles?: Subtitle[];

    survey: Survey | null;
    surveyAnswers: ParticipantAnswers | null;

    exam: Exam | null;
    examAnswers: ParticipantAnswers | null;

    currentVideoTime: number;
    resumePosition: number | null;
    /** True when the viewer already finished this on-demand — see resumePosition doc on the connection's OnDemandState. */
    resumeCompleted: boolean;
    timeline: OnDemandTimelineEvent[] | null;

    lastServerAction?: {
        method: string;
        result: BaseApiResponse | null;
    } | null;

    /** Messages this viewer sent in this session (the on-demand hub does not echo them back). */
    localSentMessages: ChatMessage[];
    lastChatRejection?: ChatRejection | null;
}

export interface OnDemandData {
    videoUrl: string;
    jsonUrl: string;
}

export interface OnDemandTimelineEvent {
    timestamp: string;
    eventType: string;
    event:
        | OnDemandTimelineSlideDeckChangedEvent
        | OnDemandTimelineSlideChangedEvent
        | OnDemandTimelineChaptersChangedEvent
        | OnDemandTimelineChapterChangedEvent
        | OnDemandTimelineChatMessageEvent
        | OnDemandTimelineBaseEvent
        | OnDemandTimelineCutsEvent
        | OnDemandTimelineVideoLayoutChangedEvent;
}

export interface OnDemandTimelineBaseEvent {
    timestamp: string;
    webinarId: string;
}

export interface OnDemandTimelineSlideDeckChangedEvent extends OnDemandTimelineBaseEvent {
    slideDeck: SlideDeck;
}

export interface OnDemandTimelineSlideChangedEvent extends OnDemandTimelineBaseEvent {
    slideNumber: number;
}

export interface OnDemandTimelineChaptersChangedEvent extends OnDemandTimelineBaseEvent {
    chapters: Chapter[];
}

export interface OnDemandTimelineChapterChangedEvent extends OnDemandTimelineBaseEvent {
    chapterId: string;
}

export interface OnDemandTimelineChatMessageEvent extends OnDemandTimelineBaseEvent {
    chatMessage: ChatMessage;
}

export interface OnDemandTimelinePollsChangedEvent extends OnDemandTimelineBaseEvent {
    polls: Poll[];
}

export interface OnDemandTimelineActivePollChangedEvent extends OnDemandTimelineBaseEvent {
    activePollId: string;
    activePollStatus: ActivePollStatus;
}

export interface OnDemandTimelineActivePollVotesChangedEvent extends OnDemandTimelineBaseEvent {
    pollId: string;
    results: { [optionId: string]: number } | { [optionId: string]: { votes: number; weightedVotes: number } };
}

export interface OnDemandTimelineCutsEvent extends OnDemandTimelineBaseEvent {
    cuts: { start: string; end: string }[];
}

export interface OnDemandTimelineVideoLayoutChangedEvent extends OnDemandTimelineBaseEvent {
    videoLayout: VideoLayout;
}

const initialState: OnDemandWebinarState = {
    isInitialized: false,

    webinarId: null,
    webinarData: null,
    onDemandData: null,

    serverTime: null,

    videoLayout: VideoLayout.Default,

    activeSlideNumber: null,
    slideDeck: {
        slides: [],
        totalSlides: 0,
        filename: null,
        status: SlideDeckStatus.None
    },
    activeChapterId: null,
    activeChapter: null,
    chapters: [],

    polls: [],
    hasVotedOnPoll: false,
    activePoll: null,
    activePollId: null,
    activePollStatus: ActivePollStatus.Inactive,
    myPollVotes: [],
    currentPolls: [],

    chatChannels: {},
    chatSettings: { enableOnDemandChatForwarding: false },
    unreadChatMessages: 0,

    subtitles: [],

    dataConnection: {
        status: LiveStatus.Offline
    },

    me: null,

    survey: null,
    surveyAnswers: null,

    exam: null,
    examAnswers: null,

    currentVideoTime: 0,
    resumePosition: null,
    resumeCompleted: false,
    timeline: null,
    localSentMessages: [],
    lastChatRejection: null
};

interface VoteOnPollRequest {
    pollId: string;
    optionId: string;
}

export const parseTimespan = (timespan: string): number => {
    const dur = Duration.fromISOTime(timespan);
    return dur.isValid ? dur.as('seconds') : 0;
};

export const toTimespan = (timestamp: Duration): string => {
    // Emit an ISO time string (hh:mm:ss.SSS) so it round-trips through parseTimespan
    // (Duration.fromISOTime) and is still parseable by .NET TimeSpan.Parse on the backend.
    // The previous 'dd.hh:mm:ss.SSSSSSS' format prepended a day component fromISOTime cannot
    // read, and mis-encoded the fraction (luxon's S token is milliseconds, not .NET ticks),
    // so a re-parse of saved timestamps decoded them all to 0.
    return timestamp.toFormat('hh:mm:ss.SSS');
};

function stringToCamelCase(s: string): string {
    return s.charAt(0).toLowerCase() + s.slice(1);
}

function objectToCamelCase(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(objectToCamelCase);
    } else if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce(
            (acc, key) => {
                const camelKey = stringToCamelCase(key);
                acc[camelKey] = objectToCamelCase(obj[key]);
                return acc;
            },
            {} as { [key: string]: any }
        );
    }
    return obj;
}

// Seeds `currentVideoTime` (and everything timeline-derived: active slide/chapter, chat backlog,
// polls) without being treated as a genuine playback report. The on-demand saga only forwards
// `onDemandCurrentVideoTimeUpdated` to the server (to create/extend a ParticipantSession) — this
// reducer backs a *second*, unwatched action (`onDemandCurrentVideoTimeSeeded`) for the two call
// sites that seed a timestamp the viewer isn't actually watching right now: the saga's initial
// seed to 0 on every (re)join, and the resume feature's seed to the resume position. Reusing
// `onDemandCurrentVideoTimeUpdated` for those used to re-report an old/zero position to the server
// on every SignalR reconnect, silently resetting the viewer's most-recently-touched session and
// making resume jump back to an earlier point after a reconnect mid-playback.
const applyCurrentVideoTime = (state: OnDemandWebinarState, action: PayloadAction<number>) => {
    state.currentVideoTime = action.payload;
    applyTimeline(state);
};

/** Derives the state at currentVideoTime from the timeline. */
const applyTimeline = (state: OnDemandWebinarState) => {
    if (!state.timeline) return;

    let slideDeck: SlideDeck = initialState.slideDeck;
    let activeSlideNumber: number | null = null;
    let chapters: ChapterWithTimestamp[] = [];
    let activeChapterId: string | null = null;
    let activePollId: string | null = null;
    let chatChannels: { [channelId: string]: ChatChannel } = {};
    let polls: PollWithVotes[] = [];
    let videoLayout: VideoLayout = VideoLayout.Default;

    // Go through the timeline to see what events have happened
    for (let i = 0; i < state.timeline.length; i++) {
        const event = state.timeline[i];
        const timestamp = parseTimespan(event.timestamp);

        // Special case for chapters, we need to calculate the timestamp for each chapter
        if (event.eventType === 'ChapterChangedEvent') {
            const chapterChangedEvent = event.event as OnDemandTimelineChapterChangedEvent;
            const chapter = chapters.find((chapter) => chapter.chapterId === chapterChangedEvent.chapterId);
            if (chapter) {
                chapter.timestamp = timestamp;
            }
        }

        if (timestamp <= state.currentVideoTime) {
            if (event.eventType === 'SlideDeckChangedEvent') {
                const slideDeckEvent = event.event as OnDemandTimelineSlideDeckChangedEvent;
                slideDeck = cloneDeep(slideDeckEvent.slideDeck);
            }
            if (event.eventType === 'SlideChangedEvent') {
                const slideChangedEvent = event.event as OnDemandTimelineSlideChangedEvent;
                activeSlideNumber = slideChangedEvent.slideNumber;
            }
            if (event.eventType === 'ChaptersChangedEvent') {
                const chaptersChangedEvent = event.event as OnDemandTimelineChaptersChangedEvent;
                chapters = chaptersChangedEvent.chapters.map((chapter) => {
                    const existing = chapters.find((c) => c.chapterId === chapter.chapterId);
                    let timestamp = 0;
                    if (existing) {
                        timestamp = existing.timestamp;
                    }
                    return { ...chapter, timestamp };
                });
            }
            if (event.eventType === 'ChapterChangedEvent') {
                const chapterChangedEvent = event.event as OnDemandTimelineChapterChangedEvent;
                activeChapterId = chapterChangedEvent.chapterId;
            }
            if (event.eventType === 'ChatMessageEvent') {
                const chatMessageEvent = event.event as OnDemandTimelineChatMessageEvent;
                const channelId = chatMessageEvent.chatMessage.channelId;
                if (!chatChannels[channelId]) {
                    chatChannels[channelId] = {
                        channelId: chatMessageEvent.chatMessage.channelId,
                        messages: [],
                        noOfUnreadMessages: 0
                    };
                }
                chatChannels[channelId].messages.push(cloneDeep(chatMessageEvent.chatMessage));
            }
            if (event.eventType === 'PollsChangedEvent') {
                const pollsChangedEvent = event.event as OnDemandTimelinePollsChangedEvent;
                polls = pollsChangedEvent.polls.map((poll) => {
                    const existingPoll = polls.find((p) => p.pollId === poll.pollId);
                    // The one setting that follows the poll as it is now, also for existing recordings
                    const currentPoll = state.currentPolls.find((p) => p.pollId === poll.pollId);
                    return {
                        ...poll,
                        showPercentagesOnly: currentPoll ? currentPoll.showPercentagesOnly : poll.showPercentagesOnly,
                        options: poll.options.map((option) => {
                            const existingOption = existingPoll?.options?.find((o) => o.optionId === option.optionId);
                            return {
                                ...option,
                                participants: [],
                                votes: existingOption?.votes || 0,
                                weightedVotes: existingOption?.weightedVotes || 0
                            };
                        }),
                        votes: {},
                        totalVotes: existingPoll?.totalVotes || 0,
                        totalWeightedVotes: existingPoll?.totalWeightedVotes || 0
                    };
                });
            }
            if (event.eventType === 'ActivePollChangedEvent') {
                const activePollChangedEvent = event.event as OnDemandTimelineActivePollChangedEvent;
                activePollId = activePollChangedEvent.activePollId;
                state.activePollStatus = activePollChangedEvent.activePollStatus;
            }
            if (event.eventType === 'ActivePollVotesChangedEvent') {
                const activePollVotesChangedEvent = event.event as OnDemandTimelineActivePollVotesChangedEvent;
                const poll = polls.find((p) => p.pollId === activePollVotesChangedEvent.pollId);
                if (!poll) {
                    continue;
                }
                for (let optionId in activePollVotesChangedEvent.results) {
                    const option = poll.options.find((o) => o.optionId === optionId);
                    if (!option) {
                        continue;
                    }

                    const votes = activePollVotesChangedEvent.results[optionId];
                    if (typeof votes === 'number') {
                        option.votes = votes;
                        option.weightedVotes = votes;
                    } else {
                        option.votes = votes.votes;
                        option.weightedVotes = votes.weightedVotes;
                    }
                }

                const myVote = state.myPollVotes.find((v) => v.pollId === poll.pollId);
                if (myVote?.clientType === ClientType.on_demand) {
                    const myOption = poll.options.find((o) => o.optionId === myVote.optionId);
                    if (myOption) {
                        myOption.weightedVotes++;
                        myOption.votes++;
                    }
                }

                poll.totalVotes = Object.values(poll.options).reduce((acc, option) => acc + option.votes, 0);
                poll.totalWeightedVotes = Object.values(poll.options).reduce((acc, option) => acc + option.weightedVotes, 0);
            }
            if (event.eventType === 'VideoLayoutChangedEvent') {
                const videoLayoutChangedEvent = event.event as OnDemandTimelineVideoLayoutChangedEvent;
                videoLayout = videoLayoutChangedEvent.videoLayout;
            }
        }
    }

    if (videoLayout === VideoLayout.Default) {
        if (slideDeck.slides.length === 0) {
            videoLayout = VideoLayout.VideoOnly;
        } else {
            videoLayout = VideoLayout.Classic;
        }
    }

    if (state.videoLayout !== videoLayout) state.videoLayout = videoLayout;
    if (!isEqual(state.slideDeck, slideDeck)) state.slideDeck = slideDeck;
    if (state.activeSlideNumber !== activeSlideNumber) state.activeSlideNumber = activeSlideNumber;
    if (!isEqual(state.chapters, chapters)) state.chapters = chapters;
    if (state.activeChapterId !== activeChapterId) state.activeChapterId = activeChapterId;
    const chapter = chapters.find((chapter) => chapter.chapterId === activeChapterId) || null;
    if (!isEqual(state.activeChapter, chapter)) state.activeChapter = chapter;
    if (!isEqual(state.chatChannels, chatChannels)) state.chatChannels = chatChannels;
    if (!isEqual(state.polls, polls)) state.polls = polls;

    const activePoll = polls.find((poll) => poll.pollId === activePollId) || null;
    if (!isEqual(state.activePoll, activePoll)) {
        state.activePoll = activePoll;
        state.activePollId = activePoll ? activePollId : null;
    }

    if (state.activeSubtitleTrackId) {
        let activeSubtitles: Subtitle[] = [];
        const track = state.subtitles.find((t) => t.trackId === state.activeSubtitleTrackId);
        if (track) {
            const current = Duration.fromObject({ seconds: state.currentVideoTime });
            track.subtitles.forEach((subtitle) => {
                const start = Duration.fromISOTime(subtitle.start);
                const end = Duration.fromISOTime(subtitle.end);

                if (current >= start && current <= end) {
                    activeSubtitles.push(subtitle);
                }
            });
        }

        if (!isEqual(state.activeSubtitles, activeSubtitles)) {
            state.activeSubtitles = activeSubtitles;
        }
    }

    const unreadChatMessages = Object.values(chatChannels).reduce((acc, channel) => acc + channel.noOfUnreadMessages, 0);
    if (state.unreadChatMessages !== unreadChatMessages) state.unreadChatMessages = unreadChatMessages;
};

export const onDemandWebinarSlice = createSlice({
    name: 'onDemandWebinar',
    initialState,
    reducers: {
        onDemandJoinWebinar: (state, action: PayloadAction<JoinWebinarRequest>) => {
            state.webinarId = action.payload.webinarId;
        },
        onDemandLeaveWebinar: (state) => {
            Object.assign(state, initialState);
        },
        onDemandInitializedWebinar: (state) => {
            state.isInitialized = true;
        },
        onDemandDataUpdated: (state, action: PayloadAction<OnDemandData>) => {
            state.isInitialized = false;
            state.onDemandData = action.payload;
        },
        onDemandWebinarDataUpdated: (state, action: PayloadAction<WebinarDataWithAssets>) => {
            state.webinarData = action.payload;
        },
        onDemandMeUpdated: (state, action: PayloadAction<Participant>) => {
            state.me = action.payload;
        },
        onDemandSurveyUpdated: (state, action: PayloadAction<Survey>) => {
            state.survey = action.payload;
        },
        onDemandSurveyAnswersUpdated: (state, action: PayloadAction<ParticipantAnswers>) => {
            state.surveyAnswers = action.payload;
        },
        onDemandExamUpdated: (state, action: PayloadAction<Exam>) => {
            state.exam = action.payload;
        },
        onDemandExamAnswersUpdated: (state, action: PayloadAction<ParticipantAnswers>) => {
            state.examAnswers = action.payload;
        },
        onDemandDataConnectionStatusUpdated: (state, action: PayloadAction<ConnectionStatus>) => {
            state.dataConnection = action.payload;
        },
        onDemandServerTimeUpdated: (state, action: PayloadAction<string>) => {
            state.serverTime = action.payload;
        },
        onDemandResumePositionReceived: (state, action: PayloadAction<number | null>) => {
            state.resumePosition = action.payload;
        },
        onDemandResumeCompletedReceived: (state, action: PayloadAction<boolean>) => {
            state.resumeCompleted = action.payload;
        },
        onDemandCurrentVideoTimeUpdated: applyCurrentVideoTime,
        // Seed-only counterpart of onDemandCurrentVideoTimeUpdated — see applyCurrentVideoTime above.
        // The saga does NOT watch this action, so dispatching it never reports a position to the server.
        onDemandCurrentVideoTimeSeeded: applyCurrentVideoTime,
        onDemandResetUnreadChatMessages: (state) => {
            state.unreadChatMessages = 0;
        },
        onDemandTimelineUpdated: (state, action: PayloadAction<OnDemandTimelineEvent[]>) => {
            state.timeline = objectToCamelCase(action.payload);
        },
        onDemandChatSettingsUpdated: (state, action: PayloadAction<ChatSettings>) => {
            state.chatSettings = action.payload;
        },
        onDemandVoteOnPoll: (state, action: PayloadAction<VoteOnPollRequest>) => {
            const votes = state.myPollVotes.filter((p) => p.pollId !== action.payload.pollId);
            votes.push({
                pollId: action.payload.pollId,
                optionId: action.payload.optionId,
                clientType: ClientType.on_demand
            });
            state.myPollVotes = votes;
        },
        onDemandMyPollVotes: (state, action: PayloadAction<PollVoteSlim[]>) => {
            state.myPollVotes = action.payload;
        },
        onDemandCurrentPollsUpdated: (state, action: PayloadAction<Poll[]>) => {
            state.currentPolls = action.payload;
            // Also arrives on a reconnect while the video is paused, with no playback report to follow
            applyTimeline(state);
        },
        onDemandSendChatMessage: (state, action: PayloadAction<{ channelId: string; message: string }>) => {},

        onDemandSetLastServerAction: (state, action: PayloadAction<{ method: string; result: BaseApiResponse | null } | null>) => {
            if (action.payload && action.payload.result && action.payload.result.success === false) {
                logError('Error occurred while processing server {Method} action, response {@Response}', {
                    Method: action.payload.method,
                    Response: action.payload.result,
                    WebinarId: state.webinarId,
                    Participant: state.me
                });
            }

            state.lastServerAction = action.payload;
        },

        onDemandChatMessageAccepted: (state, action: PayloadAction<{ channelId: string; message: string }>) => {
            if (!state.me) return;
            state.localSentMessages.push({
                timestamp: new Date().toISOString(),
                // crypto.randomUUID is undefined outside secure contexts and on Safari < 15.4; a throw here
                // would surface as a saga catch, silently dropping the accepted message from the draft.
                messageId: globalThis.crypto?.randomUUID?.() ?? `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                channelId: action.payload.channelId,
                senderClientType: ClientType.on_demand,
                senderParticipantId: state.me.participantId,
                senderNickname: state.me.nickname,
                message: action.payload.message,
                isDeleted: false,
                isPermanentlyDeleted: false
            });
        },
        onDemandChatMessageRejected: (state, action: PayloadAction<ChatRejection>) => {
            state.lastChatRejection = action.payload;
        },
        onDemandChatRejectionHandled: (state) => {
            state.lastChatRejection = null;
        },

        onDemandSubtitlesUpdated: (state, action: PayloadAction<SubtitlesTrack[]>) => {
            state.subtitles = action.payload;

            if (state.activeSubtitleTrackId) {
                const track = state.subtitles.find((t) => t.trackId === state.activeSubtitleTrackId);
                if (!track) {
                    state.activeSubtitleTrackId = null;
                    state.activeSubtitles = [];
                }
            }

            // Set default subtitle track if any
            const defaultTrack = action.payload.find((track) => track.isDefault);
            if (defaultTrack && !state.activeSubtitleTrackId) {
                state.activeSubtitleTrackId = defaultTrack.trackId;
            }
        },

        onDemandSetActiveSubtitleTrackId: (state, action: PayloadAction<string | null>) => {
            state.activeSubtitleTrackId = action.payload;
            state.activeSubtitles = [];
        },

        onDemandRefreshData: (state) => {}
    }
});

export const {
    onDemandInitializedWebinar,
    onDemandJoinWebinar,
    onDemandLeaveWebinar,
    onDemandWebinarDataUpdated,
    onDemandDataUpdated,
    onDemandMeUpdated,
    onDemandSurveyUpdated,
    onDemandSurveyAnswersUpdated,
    onDemandExamUpdated,
    onDemandExamAnswersUpdated,
    onDemandDataConnectionStatusUpdated,
    onDemandServerTimeUpdated,
    onDemandResumePositionReceived,
    onDemandResumeCompletedReceived,
    onDemandCurrentVideoTimeUpdated,
    onDemandCurrentVideoTimeSeeded,
    onDemandTimelineUpdated,
    onDemandResetUnreadChatMessages,
    onDemandVoteOnPoll,
    onDemandMyPollVotes,
    onDemandCurrentPollsUpdated,
    onDemandSendChatMessage,
    onDemandChatSettingsUpdated,
    onDemandSetLastServerAction,
    onDemandChatMessageAccepted,
    onDemandChatMessageRejected,
    onDemandChatRejectionHandled,
    onDemandRefreshData,
    onDemandSubtitlesUpdated,
    onDemandSetActiveSubtitleTrackId
} = onDemandWebinarSlice.actions;
export default onDemandWebinarSlice.reducer;
